<template>
  <section id="hero" class="relative isolate flex min-h-svh flex-col justify-center overflow-hidden bg-surface pt-28 pb-14 md:pt-32 md:pb-16">
    <!-- Patrón de líneas de contorno topográfico: decorativo, evoca el territorio y sus datos. -->
    <div class="hero-topografia" :style="{ backgroundImage: `url(${patronTopografico})` }" aria-hidden="true"></div>
    <!-- Velo que aclara el patrón detrás del titular para que el texto quede siempre legible. -->
    <div class="hero-topografia-velo" aria-hidden="true"></div>

    <div class="gob-container">
      <div class="grid gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:items-start lg:gap-14">
        <div class="flex flex-col">
          <h1 class="text-gob-8 leading-[1.02] font-black tracking-gob-tightest text-balance text-main">
            Detrás de un trámite no hay un papel,
            <span class="text-primary">hay una historia.</span>
          </h1>
          <p class="mt-6 max-w-xl text-gob-2 leading-relaxed text-[#495057]">
            Gobernar bien no es administrar recursos: es conocer, cuidar y transformar la vida de la gente.
          </p>
          <div class="mt-8">
            <GobButton to="/#que-es" con-flecha>Ver cómo funciona</GobButton>
          </div>
        </div>

        <figure class="m-0">
          <div id="hero-video-marco" class="relative aspect-3/2 overflow-hidden rounded-gob-lg bg-surface-deep shadow-strong ring-1 ring-main/10">
            <Teleport :to="destinoPantallaCompleta" :disabled="!pantallaCompleta">
              <!--
                Video y controles viajan juntos como un solo nodo: en pantalla completa el Teleport los
                traslada al <dialog>, sin desmontar el <video> ni perder currentTime/reproducción.
              -->
              <div
                ref="superficieVideo"
                class="relative size-full"
                @mousemove="mostrarControles"
                @mouseenter="mostrarControles"
                @focusin="mostrarControles"
                @touchstart.passive="mostrarControles"
              >
                <video
                  ref="video"
                  :class="['size-full', pantallaCompleta ? 'object-contain' : 'object-cover']"
                  :src="video"
                  :poster="poster"
                  width="1280"
                  height="720"
                  muted
                  loop
                  playsinline
                  preload="metadata"
                  aria-describedby="hero-video-descripcion"
                  @play="reproduciendo = true"
                  @pause="reproduciendo = false"
                  @volumechange="sincronizarSonido"
                >
                  <track
                    ref="pista"
                    kind="captions"
                    :src="pistaSubtitulos"
                    srclang="es"
                    label="Español"
                    default
                  />
                </video>

                <!-- Controles propios: el video se mueve más de 5 s, así que siempre se puede pausar. Se ocultan tras 5 s sin interacción y reaparecen con el movimiento, el foco o el tacto. -->
                <div
                  class="video-controles absolute inset-x-0 bottom-0 flex items-center gap-2 bg-linear-to-t from-surface-deep/75 to-transparent p-3 [--color-focus-ring:#FFFFFF] sm:p-4"
                  :class="{ 'video-controles-oculta': !controlesVisibles }"
                >
                  <button
                    type="button"
                    class="control-video"
                    :aria-label="reproduciendo ? 'Pausar video' : 'Reproducir video'"
                    @click="alternarReproduccion"
                  >
                    <IconPlayerPauseFilled v-if="reproduciendo" aria-hidden="true" />
                    <IconPlayerPlayFilled v-else aria-hidden="true" />
                  </button>
                  <button type="button" class="control-video px-4" @click="alternarSonido">
                    <IconVolume v-if="conSonido" aria-hidden="true" />
                    <IconVolumeOff v-else aria-hidden="true" />
                    <span class="text-sm font-semibold">{{ conSonido ? 'Silenciar' : 'Ver con sonido' }}</span>
                  </button>
                  <button
                    type="button"
                    class="control-video"
                    :aria-label="conSubtitulos ? 'Ocultar subtítulos' : 'Mostrar subtítulos'"
                    @click="alternarSubtitulos"
                  >
                    <IconSubtitles v-if="conSubtitulos" aria-hidden="true" />
                    <IconSubtitlesOff v-else aria-hidden="true" />
                  </button>
                  <button
                    ref="botonPantallaCompleta"
                    type="button"
                    class="control-video ml-auto"
                    :aria-label="pantallaCompleta ? 'Salir de pantalla completa' : 'Ver en pantalla completa'"
                    @click="pantallaCompleta ? cerrarPantallaCompleta() : abrirPantallaCompleta()"
                  >
                    <IconArrowsMinimize v-if="pantallaCompleta" aria-hidden="true" />
                    <IconArrowsMaximize v-else aria-hidden="true" />
                  </button>
                </div>
              </div>
            </Teleport>
          </div>
          <figcaption id="hero-video-descripcion" class="sr-only">
            Video de presentación de Gob 24/7 con testimonios del equipo de Qubilo y de funcionarias de la
            Alcaldía de Santiago de Cali, e imágenes de la solución en uso.
          </figcaption>
        </figure>
      </div>

      <!-- En <body>: pantalla completa propia (no la API Fullscreen del navegador), misma lógica que GobImagenAmpliable. -->
      <Teleport to="body">
        <dialog
          ref="dialogoVideo"
          class="visor-video"
          aria-label="Video de presentación de Gob 24/7 en pantalla completa"
          @close="alCerrarPantallaCompleta"
          @click="alClicFondoVideo"
        >
          <button type="button" class="visor-cerrar" aria-label="Cerrar pantalla completa" @click="cerrarPantallaCompleta">
            <IconX aria-hidden="true" />
          </button>
          <div id="hero-video-dialogo-contenido" ref="contenidoDialogoVideo" class="visor-marco-video"></div>
        </dialog>
      </Teleport>
      <!-- Reconocimiento dentro de la columna del texto -->
      <div class="mt-11 flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:gap-5">
            <img
              src="@/assets/pill_logo_premio_buen_diseno.svg"
              alt="Buen Diseño Colombia"
              class="h-14 w-auto self-start rounded-full shadow-soft ring-1 ring-main/10 sm:self-auto"
              loading="lazy"
              decoding="async"
            />
            
            <p class="leading-snug text-main">
              <strong class="block font-bold">Segundo lugar en el Premio Buen Diseño Colombia.</strong>
              <span class="text-gob-0 text-[#495057]">Categoría Diseño para la inclusión, equidad y desarrollo social.</span>
            </p>
          </div>
    </div>
  </section>
</template>

<script>
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconVolume,
  IconVolumeOff,
  IconSubtitles,
  IconSubtitlesOff,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconX
} from '@tabler/icons-vue'
import GobButton from '@/components/GobButton.vue'
import poster from '@/assets/hero-poster.webp'
import video from '@/assets/home/hero-720p.mp4'
import patronTopografico from '@/assets/home/hero-topografia.svg'

/**
 * HomeHero
 * Titular, CTA de exploración, reconocimiento del Premio Buen Diseño y video de presentación.
 * El video arranca silenciado y en bucle; con movimiento reducido no se reproduce solo, y se
 * pausa cuando sale de pantalla salvo que la persona lo haya pausado a propósito. Los subtítulos
 * en español están activos por defecto (el audio empieza silenciado) y se pueden ocultar.
 */
export default {
  name: 'HomeHero',
  components: {
    GobButton,
    IconPlayerPauseFilled,
    IconPlayerPlayFilled,
    IconVolume,
    IconVolumeOff,
    IconSubtitles,
    IconSubtitlesOff,
    IconArrowsMaximize,
    IconArrowsMinimize,
    IconX
  },
  data() {
    return {
      video,
      poster,
      patronTopografico,
      pistaSubtitulos: `${process.env.BASE_URL}captions/hero-es.vtt`,
      reproduciendo: false,
      conSonido: false,
      conSubtitulos: true,
      pantallaCompleta: false,
      /** Nodo real del <dialog> (no un selector CSS): evita la carrera de montaje entre los dos Teleport. */
      destinoPantallaCompleta: null,
      /** Se ocultan a los 5 s sin interacción; siguen visibles si un control conserva el foco. */
      controlesVisibles: true,
      /** true cuando la persona pausó: el observador ya no lo reanuda al volver a pantalla. */
      pausadoPorUsuario: false,
      autoReproducir: false
    }
  },
  mounted() {
    this.autoReproducir = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (this.autoReproducir) {
      this.reproducir()
    }
    if ('IntersectionObserver' in window) {
      this.observador = new IntersectionObserver(this.alCambiarVisibilidad, { threshold: 0.25 })
      this.observador.observe(this.$refs.video)
    }
    this.$refs.pista.track.mode = this.conSubtitulos ? 'showing' : 'hidden'
    this.reiniciarTemporizadorControles()
    // Se resuelve una sola vez a un nodo real: el otro Teleport (el <dialog>) ya montó en <body>.
    this.destinoPantallaCompleta = this.$refs.contenidoDialogoVideo
  },
  beforeUnmount() {
    if (this.observador) {
      this.observador.disconnect()
    }
    clearTimeout(this.temporizadorControles)
    // Si se sale de la ruta con el visor abierto, no dejar la página sin scroll.
    if (this.pantallaCompleta) {
      document.documentElement.style.overflow = ''
    }
  },
  methods: {
    reproducir() {
      const promesa = this.$refs.video.play()
      if (promesa) {
        // El navegador puede bloquear la reproducción; el botón queda en "Reproducir".
        promesa.catch(() => {
          this.reproduciendo = false
        })
      }
    },
    alternarReproduccion() {
      if (this.$refs.video.paused) {
        this.pausadoPorUsuario = false
        this.reproducir()
        return
      }
      this.pausadoPorUsuario = true
      this.$refs.video.pause()
    },
    /** Con sonido se reinicia: entrar a mitad de una frase narrada no se entiende. */
    alternarSonido() {
      const elemento = this.$refs.video
      if (elemento.muted) {
        elemento.muted = false
        elemento.currentTime = 0
        this.pausadoPorUsuario = false
        this.reproducir()
        return
      }
      elemento.muted = true
    },
    sincronizarSonido() {
      this.conSonido = !this.$refs.video.muted
    },
    alternarSubtitulos() {
      this.conSubtitulos = !this.conSubtitulos
      this.$refs.pista.track.mode = this.conSubtitulos ? 'showing' : 'hidden'
    },
    abrirPantallaCompleta() {
      this.pantallaCompleta = true
      document.documentElement.style.overflow = 'hidden'
      // Espera a que el Teleport traslade el video al <dialog> antes de volverlo modal.
      this.$nextTick(() => {
        this.$refs.dialogoVideo.showModal()
      })
    },
    cerrarPantallaCompleta() {
      this.$refs.dialogoVideo.close()
    },
    /** Se dispara al cerrar por cualquier vía (botón, Esc o fondo). */
    alCerrarPantallaCompleta() {
      this.pantallaCompleta = false
      document.documentElement.style.overflow = ''
      this.$refs.botonPantallaCompleta.focus({ preventScroll: true })
    },
    /** Cerrar al tocar fuera del video: el propio <dialog> o el marco que lo centra. */
    alClicFondoVideo(evento) {
      if (evento.target === this.$refs.dialogoVideo || evento.target.id === 'hero-video-dialogo-contenido') {
        this.cerrarPantallaCompleta()
      }
    },
    mostrarControles() {
      this.controlesVisibles = true
      this.reiniciarTemporizadorControles()
    },
    reiniciarTemporizadorControles() {
      clearTimeout(this.temporizadorControles)
      this.temporizadorControles = setTimeout(this.ocultarControles, 750)
    },
    /** No oculta si un control conserva el foco: no debe desaparecer un botón enfocado. */
    ocultarControles() {
      const superficie = this.$refs.superficieVideo
      if (superficie && superficie.contains(document.activeElement)) {
        this.reiniciarTemporizadorControles()
        return
      }
      this.controlesVisibles = false
    },
    alCambiarVisibilidad([entrada]) {
      if (!this.autoReproducir || this.pausadoPorUsuario) return
      if (entrada.isIntersecting) {
        this.reproducir()
      } else {
        this.$refs.video.pause()
      }
    }
  }
}
</script>

<style scoped>
.hero-topografia {
  position: absolute;
  top: 0;
  right: -60%;
  bottom: 0;
  left: 0%;
  z-index: -1;
  pointer-events: none;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  opacity:60%;
}

@media (max-width: 1023px) {
  .hero-topografia,
  .hero-topografia-velo {
    display: none;
  }
}

/*
 * Aclara el patrón justo detrás de la columna de texto (que en lg queda a la izquierda) para que
 * el titular y el párrafo mantengan contraste completo*/
.hero-topografia-velo {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgb(255 255 255 / 0.92) 0%,
    rgb(255 255 255 / 0.75) 30%,
    rgb(255 255 255 / 0.15) 62%,
    rgb(255 255 255 / 0) 78%
  );
}

@media (min-width: 1024px) {
  .hero-topografia-velo {
    background: linear-gradient(
      90deg,
      rgb(255 255 255 / 0.95) 0%,
      rgb(255 255 255 / 0.88) 40%,
      rgb(255 255 255 / 0.65) 56%,
      rgb(255 255 255 / 0.3) 68%
    ),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.65) 46px, rgb(255 255 255 / 0) 104px);
  }
}

.control-video {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.92);
  color: var(--color-main);
  cursor: pointer;
  transition: background-color var(--duration-gob) var(--ease-gob-out);
}

.control-video:hover {
  background: #FFFFFF;
}

.control-video svg {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.video-controles {
  transition: opacity var(--duration-gob) var(--ease-gob-out);
}

.video-controles-oculta {
  opacity: 0;
  pointer-events: none;
}

/* Los subtítulos nativos se pegan al borde inferior del video; se suben para no chocar con los controles. */
:deep(video)::-webkit-media-text-track-display {
  transform: translateY(-24px);
}

.visor-video {
  --color-focus-ring: #FFFFFF;
  position: fixed;
  inset: 0;
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  margin: 0;
  padding: clamp(1rem, 4vw, 3rem);
  border: 0;
  background-color: rgb(10 12 64 / 0.94);
  overflow: hidden;
}

.visor-video[open] {
  display: grid;
  animation: visor-video-aparecer var(--duration-gob) var(--ease-gob-out);
}

.visor-video::backdrop {
  background: transparent;
}

.visor-marco-video {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgb(0 0 0 / 0.45);
}

.visor-cerrar {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1;
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border-radius: 9999px;
  background-color: rgb(255 255 255 / 0.14);
  color: #FFFFFF;
  cursor: pointer;
  transition: background-color var(--duration-gob) var(--ease-gob-out);
}

.visor-cerrar:hover {
  background-color: rgb(255 255 255 / 0.26);
}

.visor-cerrar :deep(svg) {
  width: 1.5rem;
  height: 1.5rem;
}

@keyframes visor-video-aparecer {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .video-controles {
    transition: none;
  }

  .visor-video[open] {
    animation: none;
  }
}
</style>
